import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { formatNumber } from '../../lib/format'
import type { BudgetReport, FinanceTrendPoint, OrgOption, TaxComputation } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, Input, Select, Skeleton,
} from '../../components/ui'

type Tab = 'overview' | 'tax' | 'budget'

interface OverviewResponse { ok: boolean; org_id: string; orgs: OrgOption[]; trend: FinanceTrendPoint[]; latest: FinanceTrendPoint | null }
interface TaxResponse { ok: boolean; org_id: string; orgs: OrgOption[]; period: string; periods: string[]; doc: TaxComputation | null }
interface BudgetResponse {
  // Budget is hard-scoped to the caller's own cooperative server-side (no
  // org override, unlike overview/tax/payroll) - see the comment on
  // api_ops_finance_budget_get/save in routes.py.
  ok: boolean; org_id: string; period: string
  report: BudgetReport; categories: [string, string][]
}

export default function Finance() {
  const [tab, setTab] = useState<Tab>('overview')
  const [org, setOrg] = useState('')

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Finance</h1>
          <p className="page-head__sub">Revenue trend, statutory obligations and budget vs. actual.</p>
        </div>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={tab === 'overview' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('overview')}>Overview</Button>
        <Button variant={tab === 'tax' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('tax')}>Tax</Button>
        <Button variant={tab === 'budget' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('budget')}>Budget</Button>
      </div>

      {tab === 'overview' && <Overview org={org} onOrgChange={setOrg} />}
      {tab === 'tax' && <Tax org={org} onOrgChange={setOrg} />}
      {tab === 'budget' && <Budget />}
    </>
  )
}

function OrgPicker({ orgs, org, onChange }: { orgs: OrgOption[]; org: string; onChange: (v: string) => void }) {
  if (orgs.length < 2) return null
  return (
    <Select label="Cooperative" value={org} onChange={(e) => onChange(e.target.value)} style={{ maxWidth: 260 }}>
      {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </Select>
  )
}

function Overview({ org, onOrgChange }: { org: string; onOrgChange: (v: string) => void }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'finance', 'overview', org],
    queryFn: () => api.get<OverviewResponse>(`/ops/finance/overview${org ? `?org=${org}` : ''}`),
  })

  if (isError) return <Alert tone="danger">{(error as Error).message}</Alert>
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  return (
    <>
      <div style={{ marginBottom: 'var(--sp-5)' }}>
        <OrgPicker orgs={data.orgs} org={org || data.org_id} onChange={onOrgChange} />
      </div>

      {data.latest && (
        <div className="grid grid--tiles-3" style={{ marginBottom: 'var(--sp-6)' }}>
          <Stat label="Revenue this period" value={`${formatNumber(data.latest.revenue, 2)} ZMW`} />
          <Stat label="Total costs" value={`${formatNumber(data.latest.total_costs, 2)} ZMW`} />
          <Stat label="Net result" value={`${formatNumber(data.latest.net_result, 2)} ZMW`} tone={(data.latest.net_result ?? 0) >= 0 ? 'success' : 'danger'} />
        </div>
      )}

      <Card>
        <CardHeader title="Trend (last 12 periods)" />
        <CardBody flush>
          {data.trend.length === 0 ? (
            <div style={{ padding: 'var(--sp-6)' }} className="page-head__sub">No computations saved yet for this cooperative.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Period</th><th className="table__num">Revenue</th><th className="table__num">Costs</th>
                      <th className="table__num">Statutory</th><th className="table__num">Net</th></tr>
                </thead>
                <tbody>
                  {data.trend.map((t) => (
                    <tr key={t.period}>
                      <td style={{ fontWeight: 600 }}>{t.period}</td>
                      <td className="table__num">{formatNumber(t.revenue, 2)}</td>
                      <td className="table__num">{formatNumber(t.total_costs, 2)}</td>
                      <td className="table__num">{formatNumber(t.total_statutory, 2)}</td>
                      <td className="table__num">{formatNumber(t.net_result, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}

function Tax({ org, onOrgChange }: { org: string; onOrgChange: (v: string) => void }) {
  const [period, setPeriod] = useState('')
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'finance', 'tax', org, period],
    queryFn: () => api.get<TaxResponse>(
      `/ops/finance/tax?${[org && `org=${org}`, period && `period=${period}`].filter(Boolean).join('&')}`,
    ),
  })

  if (isError) return <Alert tone="danger">{(error as Error).message}</Alert>
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const doc = data.doc

  return (
    <>
      <div className="row" style={{ marginBottom: 'var(--sp-5)', gap: 'var(--sp-4)', alignItems: 'flex-end' }}>
        <OrgPicker orgs={data.orgs} org={org || data.org_id} onChange={onOrgChange} />
        {data.periods.length > 0 && (
          <Select label="Period" value={period || data.period} onChange={(e) => setPeriod(e.target.value)} style={{ maxWidth: 160 }}>
            {data.periods.map((p) => <option key={p} value={p}>{p}</option>)}
          </Select>
        )}
      </div>

      {!doc ? (
        <Alert tone="info">No tax computation saved for this period yet.</Alert>
      ) : (
        <div className="grid grid--2" style={{ gap: 'var(--sp-4)' }}>
          <Card>
            <CardHeader title="Statutory obligations" sub={`${doc.org_name} · ${doc.period}`} />
            <CardBody>
              <div className="stack">
                <Row label="Mineral royalty" value={`${formatNumber(doc.mineral_royalty?.total, 2)} ZMW`} />
                <Row label="PAYE" value={`${formatNumber(doc.paye?.total, 2)} ZMW`} />
                <Row label="NAPSA" value={`${formatNumber(doc.napsa?.total, 2)} ZMW`} />
                <Row label="NHIMA" value={`${formatNumber(doc.nhima?.total, 2)} ZMW`} />
                {doc.turnover_tax?.enabled && <Row label="Turnover tax" value={`${formatNumber(doc.turnover_tax.amount, 2)} ZMW`} />}
                <Row label="Total statutory" value={`${formatNumber(doc.summary?.total_statutory, 2)} ZMW`} strong />
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="P&L" />
            <CardBody>
              <div className="stack">
                <Row label="Revenue" value={`${formatNumber(doc.pnl?.revenue, 2)} ZMW`} />
                <Row label="Operating expenses" value={`${formatNumber(doc.pnl?.operating_expenses, 2)} ZMW`} />
                <Row label="Payroll cost" value={`${formatNumber(doc.pnl?.payroll_cost, 2)} ZMW`} />
                <Row label="Total costs" value={`${formatNumber(doc.pnl?.total_costs, 2)} ZMW`} />
                <Row label="Net result" value={`${formatNumber(doc.pnl?.net_result, 2)} ZMW`} strong />
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </>
  )
}

function Budget() {
  const qc = useQueryClient()
  const [period, setPeriod] = useState('')
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'finance', 'budget', period],
    queryFn: () => api.get<BudgetResponse>(`/ops/finance/budget${period ? `?period=${period}` : ''}`),
  })

  const save = useMutation({
    mutationFn: (categories: Record<string, number>) =>
      api.post('/ops/finance/budget', { period: period || data?.period, categories }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'finance', 'budget'] }),
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const categories: Record<string, number> = {}
    for (const [cat] of data?.categories ?? []) {
      const v = form.get(`budget_${cat}`)
      if (v) categories[cat] = Number(v)
    }
    save.mutate(categories)
  }

  if (isError) return <Alert tone="danger">{(error as Error).message}</Alert>
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const byCategory = Object.fromEntries(data.report.rows.map((r) => [r.category, r]))

  return (
    <>
      <div className="row" style={{ marginBottom: 'var(--sp-5)', gap: 'var(--sp-4)', alignItems: 'flex-end' }}>
        <Input label="Period" type="month" value={period || data.period} onChange={(e) => setPeriod(e.target.value)} style={{ maxWidth: 160 }} />
      </div>

      {save.isError && (
        <Alert tone="danger">{save.error instanceof ApiError ? save.error.message : 'Could not save the budget.'}</Alert>
      )}

      <Card>
        <CardHeader title="Budget vs. actual" sub={`Totals: ${formatNumber(data.report.totals.budget, 2)} budget · ${formatNumber(data.report.totals.actual, 2)} actual`} />
        <CardBody>
          <form onSubmit={onSubmit}>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Category</th><th className="table__num">Budget (ZMW)</th><th className="table__num">Actual</th><th className="table__num">Variance</th></tr></thead>
                <tbody>
                  {data.categories.map(([cat, label]) => {
                    const row = byCategory[cat]
                    return (
                      <tr key={cat}>
                        <td>{label}</td>
                        <td className="table__num">
                          <input className="input" name={`budget_${cat}`} type="number" step="any" min="0"
                                 defaultValue={row?.budget || ''} style={{ width: 110, textAlign: 'right' }} />
                        </td>
                        <td className="table__num">{formatNumber(row?.actual, 2)}</td>
                        <td className="table__num">{row ? formatNumber(row.variance, 2) : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="row" style={{ marginTop: 'var(--sp-4)' }}>
              <Button type="submit" loading={save.isPending}>Save budget</Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: strong ? 700 : 550 }}>{value}</span>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'success' | 'danger' }) {
  return (
    <Card pad>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{
        fontSize: 'var(--text-xl)', fontWeight: 700, marginTop: 'var(--sp-1)',
        color: tone === 'success' ? 'var(--success)' : tone === 'danger' ? 'var(--danger)' : 'var(--text)',
      }}>
        {value}
      </div>
    </Card>
  )
}
